import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { EyeOff, Lock, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AttachButton, DropZone, PendingFiles } from "@/components/chat/Attachments";
import Composer, { ModelSelect, useDefaultModel, useModels } from "@/components/chat/Composer";
import FolderPicker from "@/components/chat/FolderPicker";
import ToolsPicker from "@/components/chat/ToolsPicker";
import { useVoiceChat, VoiceBar, VoiceButton } from "@/components/chat/Voice";
import { ErrorText } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { usePendingFiles } from "@/lib/files";
import { newId, tempChats } from "@/lib/temp";
import { useTemplates } from "@/lib/templates";
import type { SendResult } from "@/lib/types";
import { primeSendResult } from "./ChatPage";

export function TempToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={clsx(
        "flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm",
        on ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800",
      )}
      title="Temporary chats are kept only in this browser"
      aria-pressed={on}
    >
      <EyeOff size={14} /> Temporary
    </button>
  );
}

const PRIVATE_KEY = "webthing.tempPrivate";

/** Private temporary chats keep their files in this browser too (uploads never reach the disk). */
export function PrivateToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={clsx(
        "flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm",
        on ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800",
      )}
      title={
        on
          ? "Private: files are kept only in this browser. The server holds them in memory while it answers, never on disk."
          : "Files are stored on the server. Turn on to keep new files only in this browser."
      }
      aria-pressed={on}
    >
      <Lock size={14} /> Private
    </button>
  );
}

function storedPrivate(): boolean {
  try {
    return localStorage.getItem(PRIVATE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function Home() {
  const me = useMe().data;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const models = useModels();
  const defaultModel = useDefaultModel();
  const [model, setModel] = useState<string | null>(null);
  const [temp, setTemp] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const modelId = model ?? defaultModel;
  const [priv, setPriv] = useState(storedPrivate);
  const pending = usePendingFiles(temp && priv);
  const [tools, setTools] = useState<string[] | null>(null);
  const [params] = useSearchParams();
  const [folder, setFolder] = useState<string | null>(params.get("folder"));
  const templates = useTemplates().data;
  const templateId = params.get("template");
  const [template, setTemplate] = useState<{ id: string; name: string; prompt: string | null } | null>(null);
  const [firstMessage, setFirstMessage] = useState("");

  // "New chat from template": fill in the template's settings once, when it is chosen.
  useEffect(() => {
    if (!templateId && template) {
      // Back on a plain "New chat": leave the template.
      setTemplate(null);
      setFirstMessage("");
      setTemp(false);
      return;
    }
    const t = templates?.find((x) => x.id === templateId);
    if (!t || !models.data || template?.id === t.id) return;
    setTemplate({ id: t.id, name: t.name, prompt: t.system_prompt });
    if (t.model_id && models.data.some((m) => m.id === t.model_id)) setModel(t.model_id);
    setTools(t.mcp_servers);
    setFolder(t.folder_id);
    setFirstMessage(t.first_message ?? "");
    setTemp(t.temporary);
  }, [templateId, templates, models.data, template]);

  function clearTemplate() {
    setTemplate(null);
    setFirstMessage("");
    setTemp(false);
    navigate("/", { replace: true });
  }

  async function send(text: string) {
    setError(null);
    try {
      if (temp) {
        const id = newId();
        await tempChats.put({
          id,
          title: text.slice(0, 60),
          model_id: modelId,
          mcp_servers: tools,
          folder_id: folder,
          system_prompt: template?.prompt ?? null,
          private: priv,
          messages: [{ id: newId(), role: "user", parts: [...(text ? [{ type: "text" as const, text }] : []), ...pending.parts], status: "done" }],
          updated_at: "",
        });
        window.dispatchEvent(new Event("temp-chats-changed"));
        pending.clear();
        navigate(`/temp/${id}`, { state: { autostart: true } });
        return;
      }
      const r = await api.post<SendResult>("/api/chats", { model_id: modelId, content: text, parts: pending.parts, settings: tools ? { mcp_servers: tools } : {}, folder_id: folder, system_prompt: template?.prompt ?? null });
      pending.clear();
      primeSendResult(qc, r);
      navigate(`/c/${r.chat.id}`);
    } catch (e) {
      setError(e);
      return false;
    }
  }

  useVoiceChat((text) => send(text), () => undefined, null);

  return (
    <DropZone onFiles={pending.add} className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto p-4">
      <div className="w-full max-w-3xl">
        <h1 className="mb-6 text-center text-3xl font-semibold tracking-tight">
          {temp ? "Temporary chat" : `Hello${me ? `, ${me.name.split(" ")[0]}` : ""}`}
        </h1>
        {temp && (
          <p className="-mt-4 mb-6 text-center text-sm text-zinc-500">
            Not saved on the server. Kept in this browser until you delete or save it.
            {priv && " Private: files stay in this browser too."}
          </p>
        )}
        {models.data && !models.data.length ? (
          <p className="text-center text-sm text-zinc-500">
            No models are available yet.{me?.role === "admin" ? " Add an endpoint under Admin → Endpoints." : " Ask an administrator to add one."}
          </p>
        ) : (
          <>
            <ErrorText error={error} />
            {template && (
              <p className="mb-2 flex items-center justify-center gap-2 text-sm text-zinc-500">
                From template <b className="font-medium text-zinc-700 dark:text-zinc-300">{template.name}</b>
                <button type="button" onClick={clearTemplate} aria-label="Stop using the template" className="rounded p-0.5 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                  <X size={14} />
                </button>
              </p>
            )}
            <VoiceBar />
            <Composer
              onSend={send}
              disabled={!modelId || pending.uploading}
              canSendEmpty={pending.parts.length > 0}
              onPasteFiles={pending.add}
              attachments={<PendingFiles files={pending.files} onRemove={pending.remove} />}
              placeholder="Ask anything"
              defaultText={firstMessage}
              toolbar={
                <>
                  <AttachButton onFiles={pending.add} />
                  <VoiceButton />
                  <ToolsPicker value={tools} onChange={setTools} />
                  <FolderPicker value={folder} onChange={setFolder} />
                  <ModelSelect value={modelId} onChange={setModel} />
                  <TempToggle on={temp} onChange={setTemp} />
                  {temp && (
                    <PrivateToggle
                      on={priv}
                      onChange={(v) => {
                        setPriv(v);
                        try {
                          localStorage.setItem(PRIVATE_KEY, v ? "1" : "0");
                        } catch {
                          /* storage unavailable */
                        }
                      }}
                    />
                  )}
                </>
              }
            />
          </>
        )}
      </div>
    </DropZone>
  );
}
